import React, { useEffect, useRef, useState } from 'react';

export type LeafletEventMarker = {
  id: string;
  title: string;
  sport?: string;
  city?: string;
  when?: string;
  lat: number;
  lng: number;
};

export default function LeafletEventsMap({
  markers,
  center,
  height = 360,
  showOnlyInView,
  onSelect,
  className,
}: {
  markers: LeafletEventMarker[];
  center: { lat: number; lng: number };
  height?: number | string;
  showOnlyInView: boolean;
  onSelect: (id: string) => void;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const nearLayerRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const [nearMeEnabled, setNearMeEnabled] = useState(false);
  const [userLoc, setUserLoc] = useState<{ lat: number; lng: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState<number>(10);
  const [locating, setLocating] = useState<boolean>(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);
  const revCacheRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    (async () => {
      const p = await checkGeoPermission();
      if (p) setPermissionState(p);
    })();
  }, []);

  useEffect(() => {
    ensureLeafletAssets();
    const check = () => {
      if ((window as any).L) setReady(true);
      else setTimeout(check, 100);
    };
    check();
  }, []);

  useEffect(() => {
    if (!ready || !containerRef.current) return;
    const L = (window as any).L;
    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current).setView([center.lat, center.lng], 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(mapRef.current);
    } else {
      mapRef.current.setView([center.lat, center.lng]);
    }

    // create/clear layer group
    if (!layerRef.current) {
      layerRef.current = L.layerGroup().addTo(mapRef.current);
    } else {
      layerRef.current.clearLayers();
    }

    // near-me overlay layer (separate, so we can manage circle/marker easily)
    if (!nearLayerRef.current) {
      nearLayerRef.current = L.layerGroup().addTo(mapRef.current);
    } else {
      nearLayerRef.current.clearLayers();
    }

    const bounds = mapRef.current.getBounds();
    const inView = showOnlyInView && bounds
      ? markers.filter(m => bounds.contains([(m as any).lat, (m as any).lng]))
      : markers;

    // near-me filter
    const filtered = nearMeEnabled && userLoc
      ? inView.filter(m => haversineKm(userLoc.lat, userLoc.lng, m.lat, m.lng) <= radiusKm)
      : inView;

    filtered.forEach((m) => {
      const marker = L.marker([m.lat, m.lng]);
      marker.on('click', () => onSelect(m.id));
      const initialCity = m.city || '';
      marker.bindPopup(popupHtml(m.title, m.sport, initialCity));
      marker.addTo(layerRef.current);
      if (!m.city) {
        reverseGeocodeCity(m.lat, m.lng, revCacheRef.current)
          .then((city) => {
            if (city) {
              try { marker.setPopupContent(popupHtml(m.title, m.sport, city)); } catch {}
            }
          })
          .catch(() => {});
      }
    });

    // draw user location + radius circle when near-me enabled
    if (nearMeEnabled && userLoc) {
      const circle = L.circle([userLoc.lat, userLoc.lng], {
        radius: radiusKm * 1000,
        color: '#0A84FF',
        weight: 2,
        fillColor: '#0A84FF',
        fillOpacity: 0.15,
      });
      circle.addTo(nearLayerRef.current);
      const youMarker = L.marker([userLoc.lat, userLoc.lng], {
        icon: L.icon({
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          shadowSize: [41, 41],
        }),
      });
      youMarker.bindPopup('<div style="font-size:12px;">You are here</div>');
      youMarker.addTo(nearLayerRef.current);
      // center map on user's location for better UX
      try {
        const currentZoom = mapRef.current?.getZoom?.() ?? 12;
        mapRef.current?.setView([userLoc.lat, userLoc.lng], Math.max(currentZoom, 13));
      } catch {}
    }

    // on moveend, re-render markers if filtering by view
    const onMoveEnd = () => {
      if (!showOnlyInView) return;
      if (!layerRef.current) return;
      layerRef.current.clearLayers();
      if (nearLayerRef.current) nearLayerRef.current.clearLayers();
      const b = mapRef.current.getBounds();
      const inView2 = markers.filter(m => b.contains([(m as any).lat, (m as any).lng]));
      const filtered2 = nearMeEnabled && userLoc
        ? inView2.filter(m => haversineKm(userLoc.lat, userLoc.lng, m.lat, m.lng) <= radiusKm)
        : inView2;
      filtered2.forEach((m) => {
        const marker = L.marker([m.lat, m.lng]);
        marker.on('click', () => onSelect(m.id));
        const initialCity = m.city || '';
        marker.bindPopup(popupHtml(m.title, m.sport, initialCity));
        marker.addTo(layerRef.current);
        if (!m.city) {
          reverseGeocodeCity(m.lat, m.lng, revCacheRef.current)
            .then((city) => {
              if (city) {
                try { marker.setPopupContent(popupHtml(m.title, m.sport, city)); } catch {}
              }
            })
            .catch(() => {});
        }
      });

      if (nearMeEnabled && userLoc) {
        const circle2 = L.circle([userLoc.lat, userLoc.lng], {
          radius: radiusKm * 1000,
          color: '#0A84FF',
          weight: 2,
          fillColor: '#0A84FF',
          fillOpacity: 0.15,
        });
        circle2.addTo(nearLayerRef.current);
        const youMarker2 = L.marker([userLoc.lat, userLoc.lng]);
        youMarker2.bindPopup('<div style="font-size:12px;">You are here</div>');
        youMarker2.addTo(nearLayerRef.current);
      }
    };

    mapRef.current.on('moveend', onMoveEnd);
    return () => {
      if (mapRef.current) mapRef.current.off('moveend', onMoveEnd);
    };
  }, [ready, center.lat, center.lng, showOnlyInView, nearMeEnabled, userLoc?.lat, userLoc?.lng, radiusKm, markers.map(m => m.id).join(',')]);

  return (
    <div style={{ position: 'relative', width: '100%' }} className={className}>
      <div
        ref={containerRef}
        style={{ height, width: '100%' }}
      />
      <NearMeControls
        enabled={nearMeEnabled}
        radiusKm={radiusKm}
        locating={locating}
        permissionState={permissionState}
        error={geoError}
        onToggle={async () => {
          if (nearMeEnabled) {
            setNearMeEnabled(false);
            setUserLoc(null);
            setGeoError(null);
            return;
          }
          setLocating(true);
          setGeoError(null);
          try {
            const pos = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
            setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            setNearMeEnabled(true);
            // center quickly for immediate feedback
            try {
              const currentZoom = mapRef.current?.getZoom?.() ?? 12;
              mapRef.current?.setView([pos.coords.latitude, pos.coords.longitude], Math.max(currentZoom, 13));
            } catch {}
          } catch (e) {
            console.warn('Geolocation error', e);
            // Fallback to approximate IP-based location if available
            const approx = await getApproximateLocation();
            if (approx) {
              setUserLoc(approx);
              setNearMeEnabled(true);
              try {
                const currentZoom = mapRef.current?.getZoom?.() ?? 12;
                mapRef.current?.setView([approx.lat, approx.lng], Math.max(currentZoom, 12));
              } catch {}
              setGeoError('Using approximate location from IP (less accurate).');
            } else {
              const msg = deriveGeoErrorMessage(e, permissionState);
              setGeoError(msg);
            }
          }
          setLocating(false);
        }}
        onRadiusChange={setRadiusKm}
      />
    </div>
  );
}

function ensureLeafletAssets() {
  if (typeof document === 'undefined') return;
  const cssId = 'leaflet-css';
  const jsId = 'leaflet-js';
  if (!document.getElementById(cssId)) {
    const link = document.createElement('link');
    link.id = cssId;
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }
  if (!document.getElementById(jsId)) {
    const script = document.createElement('script');
    script.id = jsId;
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;
    document.body.appendChild(script);
  }
}

function escapeHtml(str?: string) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function popupHtml(title: string, sport?: string, city?: string) {
  return `<div style="font-size:12px;line-height:1.4;max-width:200px;">
    <div style="font-weight:600;margin-bottom:4px;">${escapeHtml(title)}</div>
    <div style="opacity:0.8;">${escapeHtml(sport || 'Event')}</div>
    <div style="opacity:0.8;">${escapeHtml(city || '')}</div>
  </div>`;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number { return deg * Math.PI / 180; }

function getCurrentPosition(opts?: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}

async function tryGetPrecisePosition(): Promise<GeolocationPosition> {
  // 1) High accuracy with timeout
  try {
    return await getCurrentPosition({ enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 });
  } catch {}
  // 2) Low accuracy fallback
  try {
    return await getCurrentPosition({ enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 });
  } catch {}
  // 3) watchPosition quick attempt
  try {
    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
      const id = navigator.geolocation.watchPosition(
        (p) => { navigator.geolocation.clearWatch(id); resolve(p); },
        (err) => { navigator.geolocation.clearWatch(id); reject(err); },
        { enableHighAccuracy: false, maximumAge: 0 }
      );
      setTimeout(() => { try { navigator.geolocation.clearWatch(id); } catch {} reject(new Error('watchPosition timeout')); }, 8000);
    });
    return pos;
  } catch (e) {
    throw e;
  }
}

async function checkGeoPermission(): Promise<PermissionState | null> {
  try {
    // Typescript may require a cast for the descriptor name
    const res = await (navigator as any).permissions?.query?.({ name: 'geolocation' as any });
    return res?.state ?? null;
  } catch {
    return null;
  }
}

function deriveGeoErrorMessage(err: any, permissionState: PermissionState | null): string {
  if (!window.isSecureContext) {
    return 'Location requires a secure context (HTTPS or localhost).';
  }
  if (permissionState === 'denied') {
    return 'Location permission denied. Enable it in your browser settings.';
  }
  if (err?.code === 1) {
    return 'Permission denied. Please allow location access.';
  }
  if (err?.code === 2) {
    return 'Position unavailable. Try again or check your connection.';
  }
  if (err?.code === 3) {
    return 'Location request timed out. Try again.';
  }
  return 'Unable to get your location.';
}

async function getApproximateLocation(): Promise<{ lat: number; lng: number } | null> {
  try {
    // Try ipapi.co
    {
      const resp = await fetch('https://ipapi.co/json/');
      if (resp.ok) {
        const data = await resp.json();
        const lat = typeof data.latitude === 'number' ? data.latitude : Number(data.latitude);
        const lng = typeof data.longitude === 'number' ? data.longitude : Number(data.longitude);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
      }
    }
    // Try ipinfo.io
    {
      const resp = await fetch('https://ipinfo.io/json');
      if (resp.ok) {
        const data = await resp.json();
        if (typeof data.loc === 'string' && data.loc.includes(',')) {
          const [latStr, lngStr] = data.loc.split(',');
          const lat = Number(latStr);
          const lng = Number(lngStr);
          if (isFinite(lat) && isFinite(lng)) return { lat, lng };
        }
      }
    }
    // Try freegeoip.app
    {
      const resp = await fetch('https://freegeoip.app/json/');
      if (resp.ok) {
        const data = await resp.json();
        const lat = Number(data.latitude);
        const lng = Number(data.longitude);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
      }
    }
    return null;
  } catch {
    return null;
  }
}

async function reverseGeocodeCity(lat: number, lng: number, cache: Map<string, string>): Promise<string | null> {
  const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
  if (cache.has(key)) return cache.get(key) || null;
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&format=json&zoom=10&addressdetails=1`;
    const resp = await fetch(url, {
      headers: {
        'Accept-Language': 'en',
      },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    const addr = data?.address || {};
    const city = addr.city || addr.town || addr.village || addr.hamlet || addr.county || addr.state || null;
    if (city) cache.set(key, city);
    return city;
  } catch {
    return null;
  }
}

function NearMeControls({
  enabled,
  radiusKm,
  locating,
  permissionState,
  error,
  onToggle,
  onRadiusChange,
}: {
  enabled: boolean;
  radiusKm: number;
  locating: boolean;
  permissionState: PermissionState | null;
  error: string | null;
  onToggle: () => void | Promise<void>;
  onRadiusChange: (r: number) => void;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        background: 'rgba(255,255,255,0.95)',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 1000,
      }}
    >
      <button
        onClick={() => void onToggle()}
        style={{
          padding: '6px 10px',
          borderRadius: 6,
          border: '1px solid #ddd',
          background: locating ? '#ffd60a' : (enabled ? '#0A84FF' : '#fff'),
          color: locating ? '#333' : (enabled ? '#fff' : '#333'),
          fontSize: 12,
          cursor: 'pointer',
        }}
        aria-pressed={enabled}
        aria-label="Toggle near me filter"
      >
        {locating ? 'Locating…' : (enabled ? 'Near Me: On' : 'Near Me: Off')}
      </button>
      <label style={{ fontSize: 12, color: '#333' }}>
        Radius
        <input
          type="range"
          min={1}
          max={50}
          value={radiusKm}
          onChange={(e) => onRadiusChange(Number(e.target.value))}
          style={{ marginLeft: 8 }}
          aria-label="Near me radius (km)"
          disabled={locating || !enabled}
        />
        <span style={{ marginLeft: 6 }}>{radiusKm} km</span>
      </label>
      {error && (
        <span style={{ marginLeft: 8, fontSize: 12, color: '#b00020' }}>{error}</span>
      )}
      {!error && permissionState === 'prompt' && !enabled && (
        <span style={{ marginLeft: 8, fontSize: 12, color: '#555' }}>Tip: allow location when prompted.</span>
      )}
    </div>
  );
}
